"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type Convention,
  CylToRectResult,
  type PolarCtx,
  PolarToRectResult,
  RectToPolarResult,
  RectToSpaceResult,
  SphToRectResult,
  SYMBOLS,
} from "@/components/tools/linear-algebra/polar-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type AngleUnit,
  parseAngle,
} from "@/lib/tool-kit/linear-algebra/angle-input";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import { type NumberStyle, parseRational } from "@/lib/tool-kit/math/rational";

/** English strings. A translated content file overrides these through `ui`. */
export const POLAR_COORDINATES_CONVERTER_STRINGS = {
  modeLabel: "Conversion",
  modes: {
    rectToPolar: "Rectangular (x, y) → polar (r, θ)",
    polarToRect: "Polar (r, θ) → rectangular (x, y)",
    rectToSpace: "Rectangular (x, y, z) → cylindrical and spherical",
    cylToRect: "Cylindrical → rectangular (x, y, z)",
    sphToRect: "Spherical → rectangular (x, y, z)",
  },
  unitLabel: "Angle unit",
  units: { degrees: "Degrees", radians: "Radians" },
  conventionLabel: "Spherical convention",
  conventions: {
    physics: "Physics / ISO: θ is the angle from the z-axis, φ the azimuth",
    calculus:
      "Calculus (Stewart): θ is the azimuth, φ the angle from the z-axis",
  },
  radiusField: "Radius {symbol}",
  angleField: "Angle {symbol} (from the positive x-axis)",
  polarField: "Polar angle {symbol} (from the positive z-axis)",
  azimuthField: "Azimuth {symbol} (from the positive x-axis, in the xy-plane)",
  rhoField: "Distance from the z-axis {symbol}",
  angleHintDegrees: "Degrees, such as 30 or −45.",
  angleHintRadians:
    "Radians: a number such as 0.5236, or a multiple of π such as pi/6 or 2pi/3.",
  entryHint: "Whole numbers, decimals or fractions such as 3/4.",
  results: "Results",
  enterAll: "Fill in every field to see the conversion.",
  invalid:
    "Enter each value as a number or a fraction, such as 2, −0.5 or 3/4.",
  invalidAngle:
    "Enter each angle as a number; in radians you can also write pi/6 or 2pi/3.",
  origin:
    "At the origin the radius is 0 and the angle is undefined: any angle describes the same point.",
  copied: "Copied!",
  copyResults: "Copy results",
  workingHeading: "The working",
  radiusLabel: "Radius r",
  thetaLabel: "Angle θ",
  locationLabel: "Location",
  quadrantN: "Quadrant {n}",
  quadrants: { "1": "I", "2": "II", "3": "III", "4": "IV" },
  axisNames: {
    "0": "Positive x-axis",
    "90": "Positive y-axis",
    "180": "Negative x-axis",
    "270": "Negative y-axis",
  },
  radiusWorking: "r = √(x² + y²) = √({x}² + {y}²) = √{sum} = {result}",
  angleWorking:
    "θ = atan2(y, x) = atan2({y}, {x}) = {result}, taken in [0°, 360°)",
  referenceWorking:
    "The reference angle is arctan(|y/x|) ≈ {ref}°, and the point is in quadrant {quadrant}, which fixes θ.",
  equivalentForms:
    "The same point has other polar forms: (r, θ) = ({r}, {full}) after one more full turn, and ({negr}, {second}) with a negative radius. Measured from −180° to 180°, θ = {signed}.",
  polarWorking: "r = {r}, θ = {angle}",
  xWorking: "x = r cos θ = {x}",
  yWorking: "y = r sin θ = {y}",
  decimalNote: "Values are rounded to 6 decimal places.",
  negativeRadius:
    "A negative radius points opposite to the angle: the point is r units away in the direction θ + 180°.",
  cylindricalTitle: "Cylindrical {symbols}",
  sphericalTitle: "Spherical {symbols}",
  cylindricalShort: "Cylindrical",
  sphericalShort: "Spherical",
  undefinedAngle: "Undefined",
  rhoWorking: "{symbol} = √(x² + y²) = √({x}² + {y}²) = {result}",
  azimuthWorking:
    "{symbol} (azimuth) = atan2(y, x) = atan2({y}, {x}) = {result}",
  radiusWorking3D:
    "{symbol} = √(x² + y² + z²) = √({x}² + {y}² + {z}²) = {result}",
  polarWorking3D:
    "{symbol} (from the z-axis) = arccos(z / radius) = arccos({z} / {radius}) = {result}",
  onAxisNote:
    "The point is on the z-axis, so its azimuth is undefined — every azimuth gives the same point.",
  conventionNote: {
    physics:
      "Physics / ISO convention: spherical (r, θ, φ) with θ the polar angle from the z-axis (0°–180°) and φ the azimuth (0°–360°).",
    calculus:
      "Calculus convention: spherical (ρ, θ, φ) with θ the azimuth (0°–360°) and φ the polar angle from the z-axis (0°–180°). Cylindrical uses (r, θ, z).",
  },
  cylInputs: "{r} = {radius}, {a} = {angle}",
  cylX: "x = {r} cos {a} = {x}",
  cylY: "y = {r} sin {a} = {y}",
  cylZ: "z = {z}",
  sphToRectFormula:
    "x = r sin θ cos φ, y = r sin θ sin φ, z = r cos θ  (θ polar, φ azimuth)",
  sphToRectWorking: "Radius {radius}, polar angle {polar}, azimuth {azimuth}",
  plotHeading: "Diagram",
  plotTitle:
    "The point on the coordinate plane with its radius from the origin, the circle through it and the angle measured counterclockwise from the positive x-axis",
  pointText: "({x}, {y})",
  degreesUnit: "{value}°",
  radiansUnit: "{value} rad",
  downloadPng: "Download diagram (PNG)",
} as const;

export type PolarStrings = typeof POLAR_COORDINATES_CONVERTER_STRINGS;

type Mode = keyof PolarStrings["modes"];

const MODE_ORDER: Mode[] = [
  "rectToPolar",
  "polarToRect",
  "rectToSpace",
  "cylToRect",
  "sphToRect",
];
const DECIMAL_INPUT = /[.,]/;

/** The three fields each mode shows: their ids and how each is labelled. */
function fieldLabels(
  mode: Mode,
  convention: Convention,
  strings: PolarStrings
): string[] {
  const s = SYMBOLS[convention];
  switch (mode) {
    case "rectToPolar":
      return ["x", "y"];
    case "rectToSpace":
      return ["x", "y", "z"];
    case "polarToRect":
      return [
        t(strings.radiusField, { symbol: "r" }),
        t(strings.angleField, { symbol: "θ" }),
      ];
    case "cylToRect":
      return [
        t(strings.rhoField, { symbol: s.cylRadius }),
        t(strings.angleField, { symbol: s.cylAngle }),
        "z",
      ];
    default:
      return [
        t(strings.radiusField, { symbol: s.sphRadius }),
        t(strings.polarField, { symbol: s.sphPolar }),
        t(strings.azimuthField, { symbol: s.sphAzimuth }),
      ];
  }
}

const ANGLE_FIELDS: Record<Mode, number[]> = {
  rectToPolar: [],
  polarToRect: [1],
  rectToSpace: [],
  cylToRect: [1],
  sphToRect: [1, 2],
};

function Outcome({
  mode,
  values,
  ctx,
}: {
  mode: Mode;
  values: string[];
  ctx: PolarCtx;
}) {
  const { strings } = ctx;
  const count = fieldLabels(mode, ctx.convention, strings).length;
  const texts = values.slice(0, count);
  if (texts.some((text) => text.trim() === "")) {
    return <Notice>{strings.enterAll}</Notice>;
  }
  const angleFields = ANGLE_FIELDS[mode];
  const parsed = texts.map((text, i) =>
    angleFields.includes(i) ? parseAngle(text, ctx.unit) : parseRational(text)
  );
  if (parsed.some((x) => x === null)) {
    const badAngle = angleFields.some((i) => parsed[i] === null);
    return (
      <ErrorNotice>
        {badAngle ? strings.invalidAngle : strings.invalid}
      </ErrorNotice>
    );
  }
  const [a, b, c] = parsed as never[];
  switch (mode) {
    case "rectToPolar":
      return <RectToPolarResult ctx={ctx} x={a} y={b} />;
    case "polarToRect":
      return <PolarToRectResult angle={b} ctx={ctx} r={a} />;
    case "rectToSpace":
      return <RectToSpaceResult ctx={ctx} x={a} y={b} z={c} />;
    case "cylToRect":
      return <CylToRectResult angle={b} ctx={ctx} rho={a} z={c} />;
    default:
      return <SphToRectResult azimuth={c} ctx={ctx} polar={b} r={a} />;
  }
}

export default function PolarCoordinatesConverterWidget() {
  const strings = useToolUi(POLAR_COORDINATES_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("rectToPolar");
  const [unit, setUnit] = useState<AngleUnit>("degrees");
  const [convention, setConvention] = useState<Convention>("physics");
  const [values, setValues] = useState(["-3", "4", "2"]);
  const labels = fieldLabels(mode, convention, strings);
  const angleFields = ANGLE_FIELDS[mode];
  const usesSpherical =
    mode === "rectToSpace" || mode === "sphToRect" || mode === "cylToRect";
  const style: NumberStyle = values
    .slice(0, labels.length)
    .some((text) => DECIMAL_INPUT.test(text))
    ? "decimal"
    : "fraction";
  const angleHint =
    unit === "degrees" ? strings.angleHintDegrees : strings.angleHintRadians;
  const ctx: PolarCtx = {
    strings,
    style,
    mark: decimalMarkOf(fmt),
    unit,
    convention,
    fmt,
  };
  const changeMode = (next: Mode) => {
    setMode(next);
    // Sensible starting values for each conversion, in the units each field expects.
    const starts: Record<Mode, string[]> = {
      rectToPolar: ["-3", "4", "2"],
      polarToRect: ["10", unit === "degrees" ? "30" : "pi/6", "2"],
      rectToSpace: ["1", "1", "1"],
      cylToRect: ["2", unit === "degrees" ? "60" : "pi/3", "5"],
      sphToRect: [
        "4",
        unit === "degrees" ? "45" : "pi/4",
        unit === "degrees" ? "45" : "pi/4",
      ],
    };
    setValues(starts[next]);
  };
  return (
    <>
      <SelectField
        id="pc-mode"
        label={strings.modeLabel}
        onChange={changeMode}
        options={MODE_ORDER.map((id) => ({
          value: id,
          label: strings.modes[id],
        }))}
        value={mode}
      />
      <div className="flex flex-wrap gap-6">
        <PillGroup
          label={strings.unitLabel}
          name="pc-unit"
          onChange={setUnit}
          options={(["degrees", "radians"] as const).map((id) => ({
            value: id,
            label: strings.units[id],
          }))}
          value={unit}
        />
        {usesSpherical ? (
          <PillGroup
            label={strings.conventionLabel}
            name="pc-convention"
            onChange={setConvention}
            options={(["physics", "calculus"] as const).map((id) => ({
              value: id,
              label: strings.conventions[id],
            }))}
            value={convention}
          />
        ) : null}
      </div>
      <div
        className={`grid gap-4 ${labels.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3"}`}
      >
        {labels.map((label, i) => (
          <TextField
            hint={angleFields.includes(i) ? angleHint : undefined}
            id={`pc-field-${i}`}
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed positional fields
            key={i}
            label={label}
            mono
            numeric
            onChange={(value) =>
              setValues((prev) => prev.map((x, j) => (j === i ? value : x)))
            }
            value={values[i]}
          />
        ))}
      </div>
      <p className="text-muted-foreground text-xs">{strings.entryHint}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome ctx={ctx} mode={mode} values={values} />
      </section>
    </>
  );
}
